import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';

type OrderFilter = 'open' | 'all';

interface TradeOrder {
  id: string;
  user_name: string;
  symbol: string;
  side: string;
  volume: number;
  profit_amount: number;
  entry_price: number;
  status: string;
  created_at: string;
}

interface ProfileMap {
  [id: string]: string;
}

const statusStyle: Record<string, string> = {
  open: 'bg-amber-500/15 text-amber-500',
  won: 'bg-emerald-500/15 text-emerald-400',
  lost: 'bg-rose-500/15 text-rose-400',
};

const statusLabel: Record<string, string> = {
  open: 'Đang mở',
  won: 'Thắng',
  lost: 'Thua',
};

export default function AdminOrders() {
  const [filter, setFilter] = useState<OrderFilter>('open');
  const [orders, setOrders] = useState<TradeOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [autoWin, setAutoWin] = useState(false);
  const [autoLoss, setAutoLoss] = useState(false);
  const [autoCount, setAutoCount] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirming, setConfirming] = useState<{ id: string; result: 'win' | 'loss' } | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data: ordersRaw }, { data: profilesRaw }, { data: symbols }] = await Promise.all([
        supabase.rpc('get_all_orders_json'),
        supabase.rpc('get_all_profiles_json'),
        supabase.from('market_symbols').select('id, symbol'),
      ]);

      const ordersData = Array.isArray(ordersRaw) ? ordersRaw : JSON.parse((ordersRaw as string) || '[]');

      const profilesData = Array.isArray(profilesRaw) ? profilesRaw : JSON.parse((profilesRaw as string) || '[]');

      const profileMap: ProfileMap = {};
      (profilesData || []).forEach((p) => {
        profileMap[p.id] = p.full_name || p.username || 'Người dùng';
      });

      const symbolMap: Record<string, string> = {};
      (symbols || []).forEach((s) => {
        symbolMap[s.id] = s.symbol;
      });

      setOrders(
        (ordersData || []).map((o) => ({
          id: o.id,
          user_name: profileMap[o.user_id] || 'Người dùng',
          symbol: symbolMap[o.symbol_id] || '—',
          side: o.side,
          volume: Number(o.volume) || 0,
          profit_amount: Number(o.profit_amount) || 0,
          entry_price: Number(o.entry_price) || 0,
          status: o.status,
          created_at: o.created_at,
        })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải dữ liệu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filtered = orders.filter((o) => (filter === 'open' ? o.status === 'open' : true));

  const settleOrder = async (orderId: string, result: 'win' | 'loss') => {
    const { error: fnError } = await supabase.functions.invoke('settle-order', {
      body: { order_id: orderId, result },
    });
    return !fnError;
  };

  // Auto-settle effect
  useEffect(() => {
    if (!autoWin && !autoLoss) return;
    const result = autoWin ? 'win' : 'loss';

    const runAuto = async () => {
      const { data: raw } = await supabase.rpc('get_all_orders_json');
      const data = Array.isArray(raw) ? raw : JSON.parse((raw as string) || '[]');
      const open = (data || []).filter((o: any) => o.status === 'open');
      for (const o of open) {
        const ok = await settleOrder(o.id, result);
        if (ok) setAutoCount((c) => c + 1);
      }
      if (open.length > 0) await fetchOrders();
    };

    runAuto();
    const interval = setInterval(runAuto, 6000);
    return () => clearInterval(interval);
  }, [autoWin, autoLoss]);

  const openConfirm = (order: TradeOrder, result: 'win' | 'loss') => {
    setConfirming({ id: order.id, result });
  };

  const handleAutoToggle = (mode: 'win' | 'loss', next: boolean) => {
    if (mode === 'win') {
      setAutoWin(next);
      if (next) setAutoLoss(false);
    } else {
      setAutoLoss(next);
      if (next) setAutoWin(false);
    }
    if (!next) {
      setAutoCount(0);
    } else {
      setSuccess(`Bật tự động duyệt ${mode === 'win' ? 'THẮNG' : 'THUA'} — hệ thống sẽ tự chốt mỗi 6 giây.`);
    }
    setError('');
  };

  const handleSettle = async () => {
    setProcessing(confirming.id);
    setError('');
    setSuccess('');
    try {
      const { error: fnError } = await supabase.functions.invoke('settle-order', {
        body: { order_id: confirming.id, result: confirming.result },
      });
      if (fnError) throw fnError;
      setSuccess(confirming.result === 'win' ? 'Đã chốt lệnh thắng. Số dư ví người dùng đã được cộng.' : 'Đã chốt lệnh thua.');
      setConfirming(null);
      await fetchOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chốt lệnh thất bại.');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-foreground-950 mb-2">Quản lý lệnh giao dịch</h1>
        <p className="text-sm text-foreground-600 mb-6">Chốt kết quả thắng / thua cho các lệnh mua lên / mua xuống đang mở.</p>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-base"></i>
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 flex items-center gap-2">
            <i className="ri-check-line text-emerald-400 text-base"></i>
            <p className="text-sm text-emerald-400">{success}</p>
          </div>
        )}

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/60 w-fit">
            {[
              { key: 'open' as const, label: 'Đang mở' },
              { key: 'all' as const, label: 'Tất cả' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${filter === t.key ? 'bg-accent-500 text-background-950' : 'text-foreground-600 hover:text-foreground-900'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Toggle Auto Duyệt Thắng */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${autoWin ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-background-100 border-background-200/60'}`}>
            <span className={`text-xs font-semibold whitespace-nowrap ${autoWin ? 'text-emerald-500' : 'text-foreground-600'}`}>
              Auto thắng {autoWin && autoCount > 0 ? `(${autoCount})` : ''}
            </span>
            <button
              onClick={() => handleAutoToggle('win', !autoWin)}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${autoWin ? 'bg-emerald-500' : 'bg-background-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${autoWin ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Toggle Auto Duyệt Thua */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${autoLoss ? 'bg-rose-500/15 border-rose-500/40' : 'bg-background-100 border-background-200/60'}`}>
            <span className={`text-xs font-semibold whitespace-nowrap ${autoLoss ? 'text-rose-500' : 'text-foreground-600'}`}>
              Auto thua {autoLoss && autoCount > 0 ? `(${autoCount})` : ''}
            </span>
            <button
              onClick={() => handleAutoToggle('loss', !autoLoss)}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${autoLoss ? 'bg-rose-500' : 'bg-background-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${autoLoss ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-foreground-600">Đang tải...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-background-200/60 bg-background-100 py-10 text-center text-sm text-foreground-600">
            <i className="ri-inbox-line text-3xl block mb-2 text-foreground-500"></i>
            {filter === 'open' ? 'Không có lệnh đang mở' : 'Không có lệnh nào'}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-background-200/60">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Người dùng</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Công cụ</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Loại lệnh</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Vốn</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Lợi nhuận dự kiến</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Trạng thái</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Thời gian</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-t border-background-200/40 hover:bg-background-100/60 transition-colors">
                    <td className="px-4 py-3 font-semibold text-foreground-950 whitespace-nowrap">{o.user_name}</td>
                    <td className="px-4 py-3 text-foreground-800 font-semibold whitespace-nowrap">{o.symbol}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${o.side === 'up' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                        {o.side === 'up' ? 'MUA LÊN' : 'MUA XUỐNG'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground-900 whitespace-nowrap">{o.volume.toLocaleString('vi-VN')}đ</td>
                    <td className="px-4 py-3 text-right text-foreground-600 whitespace-nowrap">
                      {o.status === 'open' ? `+${o.profit_amount.toLocaleString('vi-VN')}đ` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${statusStyle[o.status]}`}>
                        {statusLabel[o.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{formatDate(o.created_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {o.status === 'open' ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openConfirm(o, 'win')}
                            disabled={processing === o.id || autoWin || autoLoss}
                            className="px-3 py-1.5 rounded-md bg-emerald-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap"
                          >
                            Thắng
                          </button>
                          <button
                            onClick={() => openConfirm(o, 'loss')}
                            disabled={processing === o.id || autoWin || autoLoss}
                            className="px-3 py-1.5 rounded-md bg-rose-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap"
                          >
                            Thua
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-foreground-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal xác nhận chốt lệnh */}
      {confirming && (() => {
        const order = orders.find((o) => o.id === confirming.id);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setConfirming(null)}>
            <div className="bg-background-100 rounded-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-foreground-950 mb-3">
                {confirming.result === 'win' ? 'Chốt lệnh THẮNG' : 'Chốt lệnh THUA'}
              </h3>
              {order && (
                <div className="space-y-2 text-sm mb-5">
                  <div className="flex justify-between">
                    <span className="text-foreground-600">Người dùng</span>
                    <span className="font-semibold text-foreground-950">{order.user_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-600">Loại lệnh</span>
                    <span className={`font-bold ${order.side === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {order.side === 'up' ? 'MUA LÊN' : 'MUA XUỐNG'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-600">Vốn</span>
                    <span className="font-semibold text-foreground-950">{order.volume.toLocaleString('vi-VN')}đ</span>
                  </div>
                  {confirming.result === 'loss' && (
                    <div className="flex justify-between">
                      <span className="text-foreground-600">Hoàn về ví</span>
                      <span className="font-semibold text-foreground-950">+{(order.volume - order.profit_amount).toLocaleString('vi-VN')}đ</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-foreground-600">Kết quả</span>
                    <span className={`font-bold ${confirming.result === 'win' ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {confirming.result === 'win'
                        ? `+${(order.volume + order.profit_amount).toLocaleString('vi-VN')}đ về ví`
                        : `-${order.profit_amount.toLocaleString('vi-VN')}đ`}
                    </span>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirming(null)} className="px-4 py-2 rounded-md bg-background-50 border border-background-300/60 text-sm font-semibold text-foreground-700 hover:bg-background-200/60 cursor-pointer whitespace-nowrap">
                  Hủy
                </button>
                <button
                  onClick={handleSettle}
                  disabled={processing === confirming.id}
                  className={`px-4 py-2 rounded-md text-sm font-semibold text-background-950 cursor-pointer whitespace-nowrap disabled:opacity-60 ${confirming.result === 'win' ? 'bg-emerald-500 hover:bg-emerald-400' : 'bg-rose-500 hover:bg-rose-400'}`}
                >
                  {processing === confirming.id ? 'Đang xử lý...' : 'Xác nhận chốt lệnh'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </AdminLayout>
  );
}